import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { createMockAuth, type MockStep } from "./mock.js";
import { createOidcAuth } from "./oidc.js";
import type { AuthAdapter, AuthConfigLike } from "./types.js";

export type MockAuth = ReturnType<typeof createMockAuth>;

interface AuthContextValue {
  auth: AuthAdapter;
  /** Present only in mock mode, for the in-app two-step journey. */
  mock: MockAuth | null;
  /** The mock journey's step, held as state so the screens follow it. */
  mockStep: MockStep;
  signedIn: boolean;
  ready: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider(props: {
  config: AuthConfigLike;
  mode: "oidc" | "mock";
  children: ReactNode;
}) {
  const mock = useMemo(() => (props.mode === "mock" ? createMockAuth() : null), [props.mode]);
  const auth = useMemo<AuthAdapter>(
    () => mock ?? createOidcAuth(props.config),
    [mock, props.config],
  );
  const [signedIn, setSignedIn] = useState(false);
  const [ready, setReady] = useState(false);
  const [mockStep, setMockStep] = useState<MockStep>("signed_out");

  useEffect(() => {
    let active = true;
    const refresh = () => {
      setMockStep(mock?.step() ?? "signed_out");
      void auth.getToken().then((token) => {
        if (!active) return;
        setSignedIn(token !== null);
        setReady(true);
      });
    };
    refresh();
    const unsubscribe = auth.subscribe(refresh);
    return () => {
      active = false;
      unsubscribe();
    };
  }, [auth, mock]);

  const value = useMemo(
    () => ({ auth, mock, mockStep, signedIn, ready }),
    [auth, mock, mockStep, signedIn, ready],
  );
  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth outside AuthProvider");
  return value;
}
